import { Button, Select } from "antd";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import { Schedule } from "entity-app/hooks/feature-activity-linking";
import { DateUtils } from "utils/dateutils";

function formatScheduleLabel(schedule: Schedule) {
  return `${DateUtils.format(schedule.schedule_date)} - ${schedule.schedule_name}`;
}

function ActivityLinksDialogHeader(props: {
  selectedSchedule: Schedule | null | undefined;
  onCancel: () => void;
  onSave: () => void;
  isLoadingScheduleList: boolean;
  isSaving: boolean;
  saveButtonDisabled: boolean;
  bidPackageName?: string;
  scheduleList?: Schedule[] | null;
  onScheduleChange?: (schedule: Schedule) => void;
}) {
  const {
    selectedSchedule,
    onCancel,
    onSave,
    isLoadingScheduleList,
    isSaving,
    saveButtonDisabled,
    bidPackageName,
    scheduleList,
    onScheduleChange
  } = props;

  const schedules = scheduleList ?? [];
  const showSchedulePicker =
    !isLoadingScheduleList &&
    !selectedSchedule?.is_active &&
    schedules.length > 0 &&
    Boolean(onScheduleChange);
  // With no schedules at all the activities table shows the empty state, so
  // the header has nothing to say about the active schedule.
  const showActiveSchedule =
    !isLoadingScheduleList && (showSchedulePicker || Boolean(selectedSchedule));

  return (
    <div className="flex items-center gap-x-4 text-xs">
      <div className="shrink-0 whitespace-nowrap font-semibold uppercase border-0 border-r-2 border-gray-400 border-solid pr-4">
        Modify Activity Links
      </div>
      <div className="grow flex items-center min-w-0 gap-x-4">
        {bidPackageName ? (
          <div
            className={
              showActiveSchedule
                ? "flex items-center min-w-0 border-0 border-r border-solid border-gray-300 pr-4"
                : "flex items-center min-w-0"
            }
          >
            <span className="mr-2 shrink-0">Bid Package:</span>
            <TruncatedTextWithTooltip
              text={bidPackageName}
              className="font-medium"
              dataTestId="activitylinksdialogheader-bid-package-name"
            />
          </div>
        ) : null}
        {showActiveSchedule ? (
          <div className="flex items-center shrink-0">
            <span className="mr-2 shrink-0">Active Schedule:</span>
            {showSchedulePicker ? (
              <Select
                className="w-64"
                size="small"
                placeholder="Select Schedule"
                value={selectedSchedule?.id}
                options={schedules.map((schedule) => ({
                  value: schedule.id,
                  label: formatScheduleLabel(schedule)
                }))}
                onChange={(scheduleId: number) => {
                  const schedule = schedules.find(
                    (item) => item.id === scheduleId
                  );
                  if (schedule) onScheduleChange?.(schedule);
                }}
                popupMatchSelectWidth={false}
                data-testid="activitylinksdialogheader-select-schedule"
              />
            ) : selectedSchedule ? (
              <span>{formatScheduleLabel(selectedSchedule)}</span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 gap-x-3">
        <Button
          className="px-6"
          onClick={onCancel}
          disabled={isSaving}
          data-testid="activitylinksdialogheader-button-cancel"
        >
          Cancel
        </Button>
        <Button
          className="px-6"
          type="primary"
          onClick={onSave}
          disabled={saveButtonDisabled}
          loading={isSaving}
          data-testid="activitylinksdialogheader-button-save"
        >
          Save
        </Button>
      </div>
    </div>
  );
}

export default ActivityLinksDialogHeader;
